import { useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const IMAGE_DELIVERY_BASE = "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q";
const FALLBACK_IMAGE = "/assets/img/offplan/01.jpg";
const DETAIL_IMAGE_VARIANT = "w=1200,sharpen=1";
const THUMB_IMAGE_VARIANT = "w=520,sharpen=1";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return baseUrl + pathname;
}

function normalizeImagePath(image, fallback = FALLBACK_IMAGE) {
  const value = String(image || "").trim();

  if (!value) {
    return fallback;
  }

  if (/^https?:\/\//i.test(value) || value.startsWith("/")) {
    return value;
  }

  if (value.startsWith("assets/")) {
    return "/" + value;
  }

  return IMAGE_DELIVERY_BASE + "/" + value.replace(/^\/+/, "");
}

function imageWithVariant(image, variant, fallback = FALLBACK_IMAGE) {
  const normalized = normalizeImagePath(image, fallback);

  if (normalized.includes("imagedelivery.net") && !normalized.includes("/w=")) {
    return normalized.replace(/\/$/, "") + "/" + variant;
  }

  return normalized;
}

function displayPrice(property) {
  const directDisplay = String(property?.displayprice || "").trim();

  if (directDisplay) {
    return /^aed/i.test(directDisplay) ? directDisplay : "AED " + directDisplay;
  }

  const numericPrice = Number(String(property?.price || "").replace(/[^\d.]/g, ""));

  if (!numericPrice) {
    return property?.price || "Price on request";
  }

  return "AED " + new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(numericPrice);
}

function field(value, fallback = "-") {
  const cleaned = String(value || "").trim();
  return cleaned || fallback;
}

function formatLocation(property) {
  return [property?.sub_community, property?.community, property?.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function referenceFromLocation(location, searchParams) {
  const queryReference = searchParams.get("id") || searchParams.get("ref") || searchParams.get("reference_number");

  if (queryReference) {
    return decodeURIComponent(queryReference).trim();
  }

  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "";

  if (lastSegment && !lastSegment.toLowerCase().endsWith(".html")) {
    return decodeURIComponent(lastSegment).trim();
  }

  return "";
}

function htmlToTextBlocks(value) {
  return String(value || "")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\r/g, "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function brochureUrl(property) {
  const brochure = String(property?.brochure || property?.Brochure || "").trim();

  if (!brochure) {
    return "";
  }

  if (/^https?:\/\//i.test(brochure) || brochure.startsWith("/")) {
    return brochure;
  }

  return "/galimg/" + brochure.replace(/^\/+/, "");
}

function itemImage(item) {
  if (!item) {
    return "";
  }

  if (typeof item === "string") {
    return item;
  }

  return item.image || item.ImagePath || item.url || item.src || "";
}

function projectImages(property) {
  const banner = property?.baner_image ? imageWithVariant(property.baner_image, DETAIL_IMAGE_VARIANT) : "";
  const photos = Array.isArray(property?.photos4) ? property.photos4 : [];
  const images = photos.map((photo) => imageWithVariant(itemImage(photo), DETAIL_IMAGE_VARIANT)).filter(Boolean);
  const unique = [...new Set([banner, ...images].filter(Boolean))];

  return unique.length ? unique : [FALLBACK_IMAGE];
}

function amenityItems(property) {
  if (Array.isArray(property?.amenities)) {
    return property.amenities.filter((amenity) => amenity?.name);
  }

  return [];
}

function mapEmbed(property) {
  return String(property?.embeddedcode || property?.map || "").trim();
}

function fallbackMapSource(property) {
  return "https://maps.google.com/maps?q=" + encodeURIComponent(formatLocation(property)) + "&z=14&output=embed";
}

function openImageGallery(event, images, startIndex = 0) {
  event.preventDefault();

  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      startIndex
    );
    return;
  }

  if (galleryItems[startIndex]) {
    window.location.href = galleryItems[startIndex].src;
  }
}

function OffPlanLeadModal({ type, property, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    unit: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  if (!type) {
    return null;
  }

  function updateField(fieldName, value) {
    setForm((current) => ({
      ...current,
      [fieldName]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("sending");
    setFeedback("");

    const payload = {
      proref: property.title || property.id || "",
      agent_name: form.name,
      agent_phone: form.phone,
      agent_email: form.email,
      agent_address: [form.unit, form.message].filter(Boolean).join(" - "),
      current_url: window.location.href,
      enqtyp: type === "brochure" ? "DOWNLOAD BROCHURE" : "REGISTER INTEREST",
      pid: property.id || "",
    };

    try {
      const response = await fetch(buildApiUrl("/api/EnquiryHandleroffplan.ashx"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Unable to submit request");
      }

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Unable to submit request");
      }

      setForm({
        name: "",
        phone: "",
        email: "",
        unit: "",
        message: "",
      });
      setStatus("success");
      setFeedback(result.message || "Request sent successfully");
    } catch (err) {
      setStatus("error");
      setFeedback(err.message || "Unable to submit request");
    }
  }

  const isBrochure = type === "brochure";

  return (
    <div className="offplan-react-modal" role="dialog" aria-modal="true">
      <button type="button" className="offplan-react-modal-backdrop" onClick={onClose} aria-label="Close popup" />
      <div className="offplan-interest-popup offplan-react-modal-panel">
        <button type="button" className="offplan-react-modal-close" onClick={onClose} aria-label="Close popup">
          <i className="fal fa-times" />
        </button>
        <form className="contact-form" onSubmit={handleSubmit}>
          <h3 className="form-title">{isBrochure ? "Download Brochure" : "Register Interest"}</h3>
          <p className="offplan-popup-text">
            {isBrochure
              ? "Share your details and our team will send the full brochure with floor plans, prices, and availability."
              : "Share your details and our off-plan specialist will contact you with floor plans, pricing, and availability."}
          </p>
          <div className="row">
            <div className="form-group col-md-6">
              <input type="text" className="form-control" value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Full Name *" required />
            </div>
            <div className="form-group col-md-6">
              <input type="tel" className="form-control" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} placeholder="Phone *" required />
            </div>
            <div className="form-group col-md-6">
              <input type="email" className="form-control" value={form.email} onChange={(event) => updateField("email", event.target.value)} placeholder="Email Address *" required />
            </div>
            <div className="form-group col-md-6">
              <select className="form-select" value={form.unit} onChange={(event) => updateField("unit", event.target.value)}>
                <option value="">Preferred Unit Type</option>
                {field(property.unittype, "") && <option value={property.unittype}>{property.unittype}</option>}
                {field(property.propertytype, "") && <option value={property.propertytype}>{property.propertytype}</option>}
              </select>
            </div>
            <div className="form-group col-12">
              <textarea className="form-control" value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Message" />
            </div>
            <div className="form-btn col-12">
              <button type="submit" className="th-btn pill text-white" disabled={status === "sending"}>
                {status === "sending" ? "Sending..." : isBrochure ? "Send Brochure" : "Submit Request"}
              </button>
            </div>
          </div>
          {feedback && <p className={"form-messages mb-0 mt-3 " + (status === "error" ? "error" : "success")}>{feedback}</p>}
        </form>
      </div>
    </div>
  );
}

function OffPlanDetails() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectId = useMemo(() => referenceFromLocation(location, searchParams), [location, searchParams]);
  const [property, setProperty] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    document.body.classList.add("offplan-details-page");

    return () => {
      document.body.classList.remove("offplan-details-page");
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadDetails() {
      if (!projectId) {
        setStatus("error");
        setError("Missing off-plan project id.");
        setProperty(null);
        return;
      }

      setStatus("loading");
      setError("");

      try {
        const response = await fetch(buildApiUrl("/api/offplandetails.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: projectId }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Unable to load off-plan details");
        }

        const payload = await response.json();
        const data = payload.result === 1 && Array.isArray(payload.data) ? payload.data[0] : null;

        if (!data) {
          throw new Error("Off-plan details not found.");
        }

        setProperty(data);
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        setProperty(null);
        setStatus("error");
        setError(err.message || "Unable to load off-plan details");
      }
    }

    loadDetails();

    return () => controller.abort();
  }, [projectId]);

  if (status === "loading") {
    return (
      <section className="offplan-section">
        <div className="container text-center">Loading off-plan details...</div>
      </section>
    );
  }

  if (status === "error" || !property) {
    return (
      <section className="offplan-section">
        <div className="container text-center">{error || "Unable to load off-plan details."}</div>
      </section>
    );
  }

  const images = projectImages(property);
  const heroImage = images[0] || FALLBACK_IMAGE;
  const featureImage = imageWithVariant(property.snd_lft_img || property.snd_background_banner || heroImage, DETAIL_IMAGE_VARIANT);
  const locationImage = imageWithVariant(property.snd_background_banner || featureImage, DETAIL_IMAGE_VARIANT);
  const descriptionBlocks = htmlToTextBlocks(property.description || property.shortdescription);
  const shortDescription = htmlToTextBlocks(property.shortdescription)[0] || descriptionBlocks[0] || field(property.subtitle, "Discover this off-plan opportunity with Blue Water Real Estate.");
  const amenities = amenityItems(property);
  const nearbyPlaces = Array.isArray(property.nearbyplace) ? property.nearbyplace : [];
  const floorPlans = Array.isArray(property.floorplan) ? property.floorplan : [];
  const floorPlanImages = floorPlans.map((plan) => imageWithVariant(itemImage(plan), DETAIL_IMAGE_VARIANT)).filter(Boolean);
  const paymentPlans = Array.isArray(property.paymentplans) ? property.paymentplans : [];
  const brochure = brochureUrl(property);
  const embeddedMap = mapEmbed(property);

  return (
    <>
      <main>
        <section className="offplan-hero">
          <img src={heroImage} alt={property.title || "Off-plan property"} />
          <div className="offplan-hero-content">
            <span className="offplan-hero-kicker">{field(property.developername, "Blue Water Real Estate")}</span>
            <h1 className="offplan-hero-title">{field(property.title, "Off-plan Property")}</h1>
            <button type="button" className="th-btn pill text-white" onClick={() => setModalType("interest")}>
              Register Interest
            </button>
          </div>
        </section>

        <section className="offplan-section">
          <div className="container">
            <h2 className="offplan-section-title">{field(property.subtitle || property.title, "Off-plan Property")}</h2>
            <p className="offplan-section-text">{shortDescription}</p>
            <div className="offplan-stat-grid">
              <div className="offplan-stat-card">
                <strong>{field(property.paymentplan)}</strong>
                <span>Payment Plan</span>
              </div>
              <div className="offplan-stat-card">
                <strong>{displayPrice(property)}</strong>
                <span>Starting Price</span>
              </div>
              <div className="offplan-stat-card">
                <strong>{field(property.propertytype)}</strong>
                <span>Property Type</span>
              </div>
              <div className="offplan-stat-card">
                <strong>{field(property.unittype)}</strong>
                <span>Unit Type</span>
              </div>
              <div className="offplan-stat-card">
                <strong>{field(property.community)}</strong>
                <span>Community</span>
              </div>
              <div className="offplan-stat-card">
                <strong>{field(property.city)}</strong>
                <span>City</span>
              </div>
            </div>
          </div>
        </section>

        <section className="offplan-section pt-0">
          <div className="container">
            <div className="offplan-feature-card">
              <img src={featureImage} alt={property.title || "Off-plan property"} />
              <div className="offplan-content-card">
                <h2>{field(property.title, "Modern Homes Designed for Connected Living")}</h2>
                {(descriptionBlocks.length ? descriptionBlocks : [shortDescription]).slice(0, 4).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <button type="button" className="th-btn pill text-white align-self-start" onClick={() => setModalType("interest")}>
                  Register Interest
                </button>
              </div>
            </div>
          </div>
        </section>

        {amenities.length > 0 && (
          <section className="offplan-section">
            <div className="container">
              <h2 className="offplan-section-title">Feature & Amenities</h2>
              <div className="offplan-amenities-grid">
                {amenities.map((amenity) => (
                  <div className="offplan-small-card" key={amenity.name}>
                    {amenity.image ? <img src={imageWithVariant(amenity.image, THUMB_IMAGE_VARIANT)} alt={amenity.name} /> : <i className="fa-solid fa-circle-check" />}
                    <span>{amenity.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {images.length > 0 && (
          <section className="offplan-section pt-0">
            <div className="container">
              <div className="offplan-gallery-grid">
                {images.slice(0, 5).map((image, index) => (
                  <a href={image} className="popular-popup-image" onClick={(event) => openImageGallery(event, images, index)} key={image}>
                    <img src={imageWithVariant(image, THUMB_IMAGE_VARIANT)} alt={(property.title || "Off-plan property") + " " + (index + 1)} />
                    {index === 0 && <span className="offplan-gallery-btn">View All Photos</span>}
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {nearbyPlaces.length > 0 && (
          <section className="offplan-section pt-0">
            <div className="container">
              <div className="offplan-attractions-layout">
                <div className="offplan-attractions-content">
                  <h2 className="offplan-section-title text-start">Nearby Attractions</h2>
                  <div className="offplan-attraction-grid">
                    {nearbyPlaces.map((place) => (
                      <div className="offplan-attraction-card" key={place.title + place.amountpercent}>
                        <i className="fa-solid fa-location-dot" />
                        <div>
                          <strong>{field(place.amountpercent)}</strong>
                          <span>{field(place.title)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="offplan-attractions-image">
                  {embeddedMap ? (
                <div className="offplan-map-embed" dangerouslySetInnerHTML={{ __html: embeddedMap }} />
              ) : (
                <iframe title={property.title || "Off-plan location"} src={fallbackMapSource(property)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              )}
                </div>
              </div>
            </div>
          </section>
        )}

        {floorPlanImages.length > 0 && (
          <section className="offplan-section offplan-muted">
            <div className="container">
              <h2 className="offplan-section-title">Floor Plan</h2>
              <div className="offplan-floor-slider-wrap offplan-floor-grid-wrap">
                <div className="offplan-floor-grid">
                  {floorPlanImages.map((image, index) => (
                    <button type="button" className="offplan-floor-card" onClick={(event) => openImageGallery(event, floorPlanImages, index)} key={image}>
                      <img src={imageWithVariant(image, THUMB_IMAGE_VARIANT)} alt={"Floor plan " + (index + 1)} />
                    </button>
                  ))}
                </div>
                
              </div>
              <div className="text-center mt-30">
                <button type="button" className="th-btn pill text-white" onClick={() => setModalType("brochure")}>
                  Download Brochure
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="offplan-section">
          <div className="container">
            <div className="offplan-location-card">
             
              <div className="offplan-content-card">
                <h2>Connected to Dubai's Best Experiences</h2>
                {htmlToTextBlocks(property.aboutlocation).slice(0, 3).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {!property.aboutlocation && <p>{formatLocation(property)}</p>}
              </div>
              <img src={locationImage} alt={property.title || "Off-plan property"} className="noradius" />
            </div>
          </div>
        </section>

        <section className="offplan-section offplan-muted">
          <div className="container">
            <h2 className="offplan-section-title">Payment Plan</h2>
            <div className="offplan-payment-grid">
              {(paymentPlans.length ? paymentPlans : [{ amountpercent: property.paymentplan, title: "Payment Plan" }, { amountpercent: displayPrice(property), title: "Starting Price" }]).map((plan) => (
                <div className="offplan-stat-card" key={field(plan.amountpercent) + field(plan.title)}>
                  <strong>{field(plan.amountpercent)}%</strong>
                  <span>{field(plan.title)}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-30">
              {brochure ? (
                <a className="th-btn pill text-white" href={brochure} target="_blank" rel="noreferrer">
                  Download Brochure
                </a>
              ) : (
                <button type="button" className="th-btn pill text-white" onClick={() => setModalType("brochure")}>
                  Download Brochure
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      <OffPlanLeadModal type={modalType} property={property} onClose={() => setModalType(null)} />

      <style>
        {" .offplan-react-modal { position: fixed; inset: 0; z-index: 9999; display: grid; place-items: center; padding: 20px; } .offplan-react-modal-backdrop { position: absolute; inset: 0; border: 0; background: rgba(0, 0, 0, 0.65); cursor: pointer; } .offplan-react-modal-panel { position: relative; display: block; width: min(760px, 100%); max-height: calc(100vh - 40px); overflow: auto; } .offplan-react-modal-close { position: absolute; right: 14px; top: 12px; width: 36px; height: 36px; border: 0; border-radius: 50%; background: #18206f; color: #fff; z-index: 2; } .offplan-location-card iframe, .offplan-map-embed iframe { width: 100%; min-height: 360px; border: 0; display: block; } .offplan-floor-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 18px; } .offplan-floor-card { border: 0; padding: 0; background: transparent; cursor: pointer; } .offplan-small-card img { width: 42px; height: 42px; object-fit: contain; } "}
      </style>
    </>
  );
}

export default OffPlanDetails;
